import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../layout/Layout';

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  html_content: string;
  template_type: string;
  created_at: string;
}

interface FilterState {
  search: string;
  verification: string;
  restriction: string;
  registrationDate: { start: string; end: string };
  age: { min: number; max: number };
  positions: number[];
  bodyTypes: number[];
  relationshipStatuses: number[];
  tags: number[];
}

interface EmailHistoryItem {
  id: number;
  subject: string;
  total_recipients: number;
  successful_sends: number;
  failed_sends: number;
  status: string;
  admin_name: string;
  template_name: string;
  created_at: string;
  started_at: string;
  completed_at: string;
}

const SendEmail: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'compose' | 'templates' | 'history'>('compose');
  
  // Template state
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    html_content: '',
    template_type: 'general'
  });
  
  // Email compose state
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    verification: 'all',
    restriction: 'all',
    registrationDate: { start: '', end: '' },
    age: { min: 0, max: 0 },
    positions: [],
    bodyTypes: [],
    relationshipStatuses: [],
    tags: []
  });
  
  const [recipientCount, setRecipientCount] = useState(0);
  
  // Sending state
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  
  // Email history state
  const [emailHistory, setEmailHistory] = useState<EmailHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Filter options
  // User selection state
  const [userSelectionMode, setUserSelectionMode] = useState<'filters' | 'users'>('filters');
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userList, setUserList] = useState<any[]>([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userListPage, setUserListPage] = useState(1);
  const [userListTotal, setUserListTotal] = useState(0);

  useEffect(() => {
    loadTemplates();
    
    // Check for pre-selected users from navigation state
    if (location.state?.userIds && location.state.userIds.length > 0) {
      loadPreSelectedUsers(location.state.userIds);
    }
  }, [location.state]);

  const loadPreSelectedUsers = async (userIds: number[]) => {
    try {
      setUserSelectionMode('users');
      // Fetch user details by IDs
      const response = await axios.post('/admin/users', {
        draw: 1,
        start: 0,
        length: userIds.length,
        search: { value: '' },
        filters: {},
        user_ids: userIds // Pass specific user IDs
      });
      
      if (response.data && response.data.data) {
        setSelectedUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading pre-selected users:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get('/admin/email-templates');
      if (response.data.success) {
        setTemplates(response.data.data.templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadFilteredUsers = useCallback(async () => {
    try {
      const response = await axios.post('/admin/bulk-email/get-filtered-users', { filters });
      if (response.data.success) {
        setRecipientCount(response.data.data.count);
      }
    } catch (error) {
      console.error('Error loading filtered users:', error);
    }
  }, [filters]);

  const loadUserList = useCallback(async () => {
    setUserListLoading(true);
    try {
      const response = await axios.post('/admin/users', {
        draw: userListPage,
        start: (userListPage - 1) * 10,
        length: 10,
        search: { value: userSearch },
        order: [{ column: 'name', dir: 'asc' }],
        columns: [],
        filters: {}
      });
      
      if (response.data.data) {
        setUserList(response.data.data);
        setUserListTotal(response.data.recordsTotal);
      }
    } catch (error) {
      console.error('Error loading user list:', error);
    } finally {
      setUserListLoading(false);
    }
  }, [userListPage, userSearch]);

  const loadEmailHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.post('/admin/bulk-email/history', {
        draw: historyPage,
        start: (historyPage - 1) * 10,
        length: 10,
        search: { value: '' },
        order: [{ column: 'created_at', dir: 'desc' }]
      });
      
      setEmailHistory(response.data.data);
      setHistoryTotal(response.data.recordsTotal);
    } catch (error) {
      console.error('Error loading email history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadEmailHistory();
    }
  }, [activeTab, loadEmailHistory]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (activeTab === 'compose') {
        if (userSelectionMode === 'filters') {
          loadFilteredUsers();
        } else {
          loadUserList();
        }
      }
    }, 500);
    return () => clearTimeout(debounce);
  }, [activeTab, loadFilteredUsers, loadUserList, userSelectionMode]);

  const handleTemplateSelect = (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplateId(templateId);
      setSubject(template.subject);
      setHtmlContent(template.html_content);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplateId) {
        // Update existing template
        const response = await axios.put(`/admin/email-templates/${editingTemplateId}`, templateForm);
        if (response.data.success) {
          alert('Template updated successfully');
          loadTemplates();
          setShowTemplateModal(false);
          setEditingTemplateId(null);
          setTemplateForm({ name: '', subject: '', html_content: '', template_type: 'general' });
        }
      } else {
        // Create new template
        const response = await axios.post('/admin/email-templates', templateForm);
        if (response.data.success) {
          alert('Template saved successfully');
          loadTemplates();
          setShowTemplateModal(false);
          setTemplateForm({ name: '', subject: '', html_content: '', template_type: 'general' });
        }
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save template');
    }
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplateId(template.id);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      template_type: template.template_type
    });
    setShowTemplateModal(true);
  };

  const handleSendTest = async () => {
    if (!subject || !htmlContent) {
      alert('Please enter subject and content');
      return;
    }

    try {
      const response = await axios.post('/admin/bulk-email/send-test', {
        subject,
        html_content: htmlContent
      });
      
      if (response.data.success) {
        alert(response.data.message);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send test email');
    }
  };

  const handleSendBulk = async () => {
    if (!subject || !htmlContent) {
      alert('Please enter subject and content');
      return;
    }

    const currentRecipientCount = getRecipientCount();
    if (currentRecipientCount === 0) {
      alert('No recipients selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to send this email to ${currentRecipientCount} recipients?`)) {
      return;
    }

    setIsSending(true);
    setSendProgress(0);

    try {
      const requestData: any = {
        subject,
        html_content: htmlContent,
        template_id: selectedTemplateId
      };

      if (userSelectionMode === 'filters') {
        requestData.filters = filters;
      } else {
        requestData.user_ids = selectedUsers.map(u => u.id);
      }

      const response = await axios.post('/admin/bulk-email/send', requestData);

      if (response.data.success) {
        alert(response.data.message);
        // Reset form
        setSubject('');
        setHtmlContent('');
        setSelectedTemplateId(null);
        setSelectedUsers([]);
        setFilters({
          search: '',
          verification: 'all',
          restriction: 'all',
          registrationDate: { start: '', end: '' },
          age: { min: 0, max: 0 },
          positions: [],
          bodyTypes: [],
          relationshipStatuses: [],
          tags: []
        });
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send emails');
    } finally {
      setIsSending(false);
      setSendProgress(0);
    }
  };

  const handleUserSelect = (user: any) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === userList.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...userList]);
    }
  };

  const handleRemoveSelectedUser = (userId: number) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  // Calculate recipient count based on selection mode
  const getRecipientCount = () => {
    if (userSelectionMode === 'filters') {
      return recipientCount;
    } else {
      return selectedUsers.length;
    }
  };

  const handleClearSelectedUsers = () => {
    setSelectedUsers([]);
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await axios.delete(`/admin/email-templates/${id}`);
      loadTemplates();
      if (selectedTemplateId === id) {
        setSelectedTemplateId(null);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete template');
    }
  };

  return (
    <Layout>
      <style>{`
        .template-var {
          cursor: pointer;
          padding: 2px 6px;
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          transition: all 0.2s ease;
        }
        .template-var:hover {
          background-color: #e9ecef;
          border-color: #006175;
          color: #006175;
        }
      `}</style>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Send Email</h2>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'compose' ? 'active' : ''}`}
              onClick={() => setActiveTab('compose')}
            >
              <i className="bi bi-envelope me-2"></i>Compose
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              <i className="bi bi-file-text me-2"></i>Templates
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <i className="bi bi-clock-history me-2"></i>History
            </button>
          </li>
        </ul>

        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <div className="row">
            <div className="col-md-3">
              {/* Selection Mode Toggle */}
              <div className="card mb-3">
                <div className="card-header">
                  <h5 className="mb-0">Recipient Selection</h5>
                </div>
                <div className="card-body">
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${userSelectionMode === 'filters' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setUserSelectionMode('filters')}
                    >
                      <i className="bi bi-funnel me-2"></i>Filters
                    </button>
                    <button
                      type="button"
                      className={`btn ${userSelectionMode === 'users' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setUserSelectionMode('users')}
                    >
                      <i className="bi bi-people me-2"></i>Select Users
                    </button>
                  </div>
                </div>
              </div>

              {/* Filters Section */}
              {userSelectionMode === 'filters' && (
                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="mb-0">Filters</h5>
                  </div>
                  <div className="card-body">
                    {/* Search */}
                    <div className="mb-3">
                      <label className="form-label">Search</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name, email, ID..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      />
                    </div>

                    {/* Verification Status */}
                    <div className="mb-3">
                      <label className="form-label">Verification</label>
                      <select
                        className="form-select"
                        value={filters.verification}
                        onChange={(e) => setFilters({ ...filters, verification: e.target.value })}
                      >
                        <option value="all">All</option>
                        <option value="verified">Verified</option>
                        <option value="not_verified">Not Verified</option>
                      </select>
                    </div>

                    {/* Restriction Status */}
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={filters.restriction}
                        onChange={(e) => setFilters({ ...filters, restriction: e.target.value })}
                      >
                        <option value="all">All</option>
                        <option value="not_banned">Active</option>
                        <option value="banned">Banned</option>
                      </select>
                    </div>

                    {/* Registration Date */}
                    <div className="mb-3">
                      <label className="form-label">Registration Date</label>
                      <input
                        type="date"
                        className="form-control mb-2"
                        value={filters.registrationDate.start}
                        onChange={(e) => setFilters({
                          ...filters,
                          registrationDate: { ...filters.registrationDate, start: e.target.value }
                        })}
                      />
                      <input
                        type="date"
                        className="form-control"
                        value={filters.registrationDate.end}
                        onChange={(e) => setFilters({
                          ...filters,
                          registrationDate: { ...filters.registrationDate, end: e.target.value }
                        })}
                      />
                    </div>

                    {/* Age Range */}
                    <div className="mb-3">
                      <label className="form-label">Age Range</label>
        <div className="row">
                        <div className="col-6">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Min"
                            value={filters.age.min || ''}
                            onChange={(e) => setFilters({
                              ...filters,
                              age: { ...filters.age, min: parseInt(e.target.value) || 0 }
                            })}
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Max"
                            value={filters.age.max || ''}
                            onChange={(e) => setFilters({
                              ...filters,
                              age: { ...filters.age, max: parseInt(e.target.value) || 0 }
                            })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Clear Filters */}
                    <button
                      className="btn btn-sm btn-outline-secondary w-100"
                      onClick={() => setFilters({
                        search: '',
                        verification: 'all',
                        restriction: 'all',
                        registrationDate: { start: '', end: '' },
                        age: { min: 0, max: 0 },
                        positions: [],
                        bodyTypes: [],
                        relationshipStatuses: [],
                        tags: []
                      })}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}

              {/* User Selection Section */}
              {userSelectionMode === 'users' && (
                <div className="card mb-3">
                  <div className="card-header">
                    <h5 className="mb-0">Search Users</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                    
                    {userListLoading ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm" role="status"></div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleSelectAllUsers}
                          >
                            {selectedUsers.length === userList.length ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                        
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {userList.map(user => (
                            <div key={user.id} className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedUsers.some(u => u.id === user.id)}
                                onChange={() => handleUserSelect(user)}
                              />
                              <label className="form-check-label">
                                <div>
                                  <strong>{user.name}</strong>
                                  <br />
                                  <small className="text-muted">{user.email}</small>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>

                        {/* Pagination */}
                        {userListTotal > 10 && (
                          <nav>
                            <ul className="pagination pagination-sm justify-content-center">
                              <li className={`page-item ${userListPage === 1 ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setUserListPage(userListPage - 1)}
                                >
                                  Previous
                                </button>
                              </li>
                              <li className="page-item active">
                                <span className="page-link">{userListPage}</span>
                              </li>
                              <li className={`page-item ${userListPage * 10 >= userListTotal ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setUserListPage(userListPage + 1)}
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </nav>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Selected Users Summary */}
              {userSelectionMode === 'users' && selectedUsers.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Selected Users</h5>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleClearSelectedUsers}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="card-body">
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {selectedUsers.map(user => (
                        <div key={user.id} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
              <div>
                            <strong>{user.name}</strong>
                            <br />
                            <small className="text-muted">{user.email}</small>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveSelectedUser(user.id)}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Recipient Count */}
              <div className="card">
                <div className="card-body text-center">
                  <h3 className="mb-0">{getRecipientCount()}</h3>
                  <small className="text-muted">Recipients</small>
                </div>
              </div>
            </div>

            <div className="col-md-9">
              <div className="card">
                <div className="card-body">
                  {/* Template Selection */}
                  <div className="mb-3">
                    <label className="form-label">Load Template</label>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select"
                        value={selectedTemplateId || ''}
                        onChange={(e) => handleTemplateSelect(parseInt(e.target.value))}
                      >
                        <option value="">Select a template...</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name} ({template.template_type})
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => setShowTemplateModal(true)}
                      >
                        <i className="bi bi-plus-circle"></i> New
                      </button>
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="mb-3">
                    <label className="form-label">Subject *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Enter email subject"
                    />
                  </div>

                  {/* Content */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0">Content (HTML) *</label>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setShowPreview(!showPreview)}
                      >
                        {showPreview ? 'Edit' : 'Preview'}
                      </button>
                    </div>
                    
                    {!showPreview ? (
                      <textarea
                        className="form-control"
                        rows={12}
                        value={htmlContent}
                        onChange={(e) => setHtmlContent(e.target.value)}
                        placeholder="Enter HTML content or plain text"
                      />
                    ) : (
                      <div
                        className="border p-3"
                        style={{ minHeight: '300px', backgroundColor: '#fff' }}
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                      />
                    )}
                  </div>

                  {/* Template Variables Helper */}
                  <div className="mb-3">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">
                          <button
                            className="btn btn-link p-0 text-decoration-none"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#templateVariables"
                            aria-expanded="false"
                          >
                            <i className="bx bx-code-alt me-2"></i>
                            Template Variables (click to copy)
                          </button>
                        </h6>
                      </div>
                      <div className="collapse" id="templateVariables">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <h6 className="text-primary">Basic Info</h6>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{name}}')}>{'{{name}}'}</code>
                                <small className="text-muted ms-2">User's full name</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{email}}')}>{'{{email}}'}</code>
                                <small className="text-muted ms-2">User's email address</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{dob}}')}>{'{{dob}}'}</code>
                                <small className="text-muted ms-2">Date of birth</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{age}}')}>{'{{age}}'}</code>
                                <small className="text-muted ms-2">User's age</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{account_number}}')}>{'{{account_number}}'}</code>
                                <small className="text-muted ms-2">Account number</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{phone}}')}>{'{{phone}}'}</code>
                                <small className="text-muted ms-2">Phone number</small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <h6 className="text-primary">Profile Info</h6>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{position}}')}>{'{{position}}'}</code>
                                <small className="text-muted ms-2">Position preference</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{body_type}}')}>{'{{body_type}}'}</code>
                                <small className="text-muted ms-2">Body type</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{relationship_status}}')}>{'{{relationship_status}}'}</code>
                                <small className="text-muted ms-2">Relationship status</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{location}}')}>{'{{location}}'}</code>
                                <small className="text-muted ms-2">User location</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{created_at}}')}>{'{{created_at}}'}</code>
                                <small className="text-muted ms-2">Registration date</small>
                              </div>
                              <div className="mb-2">
                                <code className="template-var" onClick={() => navigator.clipboard.writeText('{{last_login}}')}>{'{{last_login}}'}</code>
                                <small className="text-muted ms-2">Last login date</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleSendTest}
                      disabled={isSending || !subject || !htmlContent}
                    >
                      <i className="bi bi-send me-2"></i>Send Test
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSendBulk}
                      disabled={isSending || !subject || !htmlContent || getRecipientCount() === 0}
                    >
                      {isSending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill me-2"></i>
                          Send to {getRecipientCount()} Recipients
                        </>
                      )}
                    </button>
                  </div>

                  {/* Progress Bar */}
                  {isSending && (
                    <div className="mt-3">
                      <div className="progress">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: `${sendProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Email Templates</h5>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowTemplateModal(true)}
                >
                  <i className="bi bi-plus-circle me-2"></i>New Template
                </button>
        </div>

              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Subject</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map(template => (
                      <tr key={template.id}>
                        <td>{template.name}</td>
                        <td>
                          <span className={`badge bg-${
                            template.template_type === 'promotional' ? 'success' :
                            template.template_type === 'warning' ? 'warning' :
                            template.template_type === 'info' ? 'info' : 'secondary'
                          }`}>
                            {template.template_type}
                          </span>
                        </td>
                        <td>{template.subject}</td>
                        <td>{new Date(template.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditTemplate(template)}
                              title="Edit Template"
                            >
                              <i className="bx bx-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTemplate(template.id)}
                              title="Delete Template"
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
            <div className="card">
              <div className="card-body">
              <h5 className="mb-3">Email History</h5>

              {historyLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status"></div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Subject</th>
                          <th>Recipients</th>
                          <th>Success</th>
                          <th>Failed</th>
                          <th>Status</th>
                          <th>Admin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailHistory.map(email => (
                          <tr key={email.id}>
                            <td>{new Date(email.created_at).toLocaleString()}</td>
                            <td>{email.subject}</td>
                            <td>{email.total_recipients}</td>
                            <td>
                              <span className="badge bg-success">{email.successful_sends}</span>
                            </td>
                            <td>
                              <span className="badge bg-danger">{email.failed_sends}</span>
                            </td>
                            <td>
                              <span className={`badge bg-${
                                email.status === 'completed' ? 'success' :
                                email.status === 'in_progress' ? 'warning' :
                                email.status === 'failed' ? 'danger' : 'secondary'
                              }`}>
                                {email.status}
                              </span>
                            </td>
                            <td>{email.admin_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {historyTotal > 10 && (
                    <nav>
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${historyPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setHistoryPage(historyPage - 1)}
                          >
                            Previous
                          </button>
                        </li>
                        <li className="page-item active">
                          <span className="page-link">{historyPage}</span>
                        </li>
                        <li className={`page-item ${historyPage * 10 >= historyTotal ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setHistoryPage(historyPage + 1)}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Template Modal */}
        {showTemplateModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingTemplateId ? 'Edit' : 'New'} Email Template</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowTemplateModal(false);
                      setEditingTemplateId(null);
                      setTemplateForm({ name: '', subject: '', html_content: '', template_type: 'general' });
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Template Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Template Type</label>
                    <select
                      className="form-select"
                      value={templateForm.template_type}
                      onChange={(e) => setTemplateForm({ ...templateForm, template_type: e.target.value })}
                    >
                      <option value="general">General</option>
                      <option value="promotional">Promotional</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Subject *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">HTML Content *</label>
                    <textarea
                      className="form-control"
                      rows={8}
                      value={templateForm.html_content}
                      onChange={(e) => setTemplateForm({ ...templateForm, html_content: e.target.value })}
                    />
                  </div>

                  {/* Template Variables in Modal */}
                  <div className="mb-3">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="bx bx-code-alt me-2"></i>
                          Available Template Variables (click to copy)
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <h6 className="text-primary small">Basic Info</h6>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{name}}')}>{'{{name}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{email}}')}>{'{{email}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{dob}}')}>{'{{dob}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{age}}')}>{'{{age}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{account_number}}')}>{'{{account_number}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{phone}}')}>{'{{phone}}'}</code>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-primary small">Profile Info</h6>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{position}}')}>{'{{position}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{body_type}}')}>{'{{body_type}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{relationship_status}}')}>{'{{relationship_status}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{location}}')}>{'{{location}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{created_at}}')}>{'{{created_at}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{last_login}}')}>{'{{last_login}}'}</code>
                            </div>
                          </div>
                        </div>
                        <small className="text-muted">Click any variable to copy it, then paste in subject or content.</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowTemplateModal(false);
                      setEditingTemplateId(null);
                      setTemplateForm({ name: '', subject: '', html_content: '', template_type: 'general' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveTemplate}
                    disabled={!templateForm.name || !templateForm.subject || !templateForm.html_content}
                  >
                    {editingTemplateId ? 'Update Template' : 'Save Template'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SendEmail;
